import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { AUTOSAVE_DELAY_MS, SESSION_TICK_MS, evaluateMetrics, isSinclairEmail } from "@context/shared";
import type { SubmissionReview, WritingEvent } from "@context/shared";
import { ApiClient, ApiClientError, isAbortError } from "../api/client";
import {
  createSessionId,
  clearSessions,
  deleteSession as removeStoredSession,
  saveSession as persistDraftSession,
} from "./draftSessions";
import type { DraftSession } from "./draftSessions";
import { createInitialState, studentReducer } from "./studentReducer";
import type { MutationSource, StudentAction, StudentState } from "./studentReducer";
import { submissionPdfFileName, triggerBlobDownload } from "../lib/downloadBlob";

/** How long the manual-save button shows its confirmation, as in the prototype. */
const SAVE_CONFIRM_MS = 1600;

/**
 * The events endpoint rejects batches larger than this (see `eventBatchSchema`
 * in apps/api/src/schemas, `z.array(...).max(200)`), so a flush splits the
 * buffer into sequential chunks of at most this many events.
 */
const MAX_EVENTS_PER_BATCH = 200;

/**
 * How long submit's pre-sync waits on an unrelated in-flight draft/prompt PUT
 * before giving up and continuing. On timeout the stuck request is ABORTED (so
 * it can never land after the successor is saved), the chain is dropped, and
 * the submit proceeds with a stall note instead of hanging.
 */
const SYNC_DRAIN_TIMEOUT_MS = 3000;

/**
 * Shown when the pre-sync drain gives up on a stuck background sync. Unlike the
 * generic error copy it does not tell the student to retry — the submission is
 * proceeding normally and will likely succeed — and `submitted` (or the flow's
 * own result) clears it once the submit actually lands.
 */
const SYNC_STALL_GUIDANCE = "A background sync stalled; continuing your submission…";

export interface StudentActions {
  setProfessorEmail(value: string): void;
  setStudentEmail(value: string): void;
  setReferencePrompt(value: string): void;
  startAssignment(): Promise<void>;
  saveDraft(): Promise<void>;
  loadSession(id: string): Promise<void>;
  deleteSession(id: string): void;
  submit(): Promise<void>;
  recordMutation(input: { html: string; text: string; source: MutationSource; paragraphCount: number }): void;
  recordKeydown(key: string): void;
  recordPaste(characters: number, words: number): void;
  recordFocus(): void;
  recordBlur(): void;
  setGuidance(message: string | null): void;
  /** Returns the stored PDF report as a Blob, or null if it could not be fetched. */
  downloadPdf(submissionId: string): Promise<Blob | null>;
}

export interface StudentContextValue {
  state: StudentState;
  /** Live review recomputed from current metrics with the shared rules. */
  review: SubmissionReview;
  actions: StudentActions;
  /** The service client, shared with panels that do not use the draft state. */
  client: ApiClient;
}

const StudentContext = createContext<StudentContextValue | null>(null);

export interface StudentProviderProps {
  client: ApiClient;
  children: ReactNode;
  /** Disables the 1 Hz session clock; used by tests that drive time manually. */
  autoStartClock?: boolean;
}

export function StudentProvider({ client, children, autoStartClock = true }: StudentProviderProps) {
  const [state, setState] = useState<StudentState>(createInitialState);

  // `stateRef` mirrors the reducer state at DISPATCH time, not render time.
  // React only commits `state` on render, so a dispatch's effect on
  // `pendingEvents` would otherwise be invisible until the next commit — an
  // event dispatched mid-submit whose render has not flushed would be missing
  // from the flush's snapshot and silently dropped by the submitted wipe. The
  // reducer runs once here and the result is stored synchronously, so
  // `stateRef` is ALWAYS authoritative (including dispatched-but-uncommitted
  // events). Every dispatch goes through `dispatch` below.
  const stateRef = useRef(state);
  const dispatch = useCallback((action: StudentAction) => {
    stateRef.current = studentReducer(stateRef.current, action);
    setState(stateRef.current);
  }, []);

  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Separate from `syncTimer` so a prompt edit inside the autosave window can
  // never cancel a queued draft/email sync (or skip the pending event flush).
  const promptTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True while a submit flow is running, so a double-click cannot dispatch the
  // confirmation or download the PDF twice.
  const submittingRef = useRef(false);
  // Monotonically increasing submit generation. Bumped the moment a submit
  // starts (before its pre-sync), so anything that captured the epoch before
  // the submit — and only re-checks `submittingRef`, which a completed submit
  // clears — can detect that a submit happened at all.
  const submitEpochRef = useRef(0);
  // True while an events POST is in flight, so a concurrent flush coalesces
  // into the one that is already running instead of double-posting the buffer.
  // Resolves when the running flush's whole chain (including its deferred
  // re-run) has settled, with the true remainder. A caller that arrives
  // mid-flight awaits this chain; while it is set, only one events POST is in
  // flight at a time. Cleared once the chain settles so the next flush starts
  // a fresh chain.
  const flushingRef = useRef<Promise<WritingEvent[]> | null>(null);
  // Set by a flush that arrives mid-flight; the running flush re-runs once its
  // POST settles to pick up anything queued in the meantime.
  const flushAgainRef = useRef(false);
  // The events the most recent flight actually delivered. A coalesced re-run
  // must exclude these from its snapshot: until React commits `eventsFlushed`
  // the live buffer still holds them, so without the exclusion the re-run would
  // re-post the batch that just reached the service.
  const lastSentRef = useRef<WritingEvent[] | null>(null);

  // The most recent draft/prompt sync that is still settling. Draft and prompt
  // PUTs are serialized: each new sync chains behind the previous one (see
  // runSynced), so at most one PUT is on the wire and this always points at the
  // newest chain. submit() awaits it before its pre-sync, and saveDraft and
  // restoreSession await it after their epoch re-checks, so a PUT that was
  // already in flight when one of those flows began can never resolve out of
  // order — in particular a draft PUT started before a submit can never land
  // after submitDraft saved the successor (the server has no generation check;
  // last writer wins).
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  // The AbortController for the CURRENT lineage of draft/prompt syncs: every
  // sync that chains behind an in-flight one shares its controller (see
  // runSynced), so submit's drain can cancel the WHOLE lineage — including a
  // blackholed PUT that is actually on the wire and any follower still waiting
  // behind it — rather than abandoning a stuck request that could land after
  // the successor is created.
  const syncLineageAbortRef = useRef<AbortController | null>(null);

  /**
   * Runs a draft/prompt PUT as part of the single serialized sync chain. The
   * operation receives the lineage's AbortSignal so its request can be cancelled
   * by a submit drain; an operation that was queued behind an earlier PUT of an
   * aborted lineage never starts at all.
   */
  const runSynced = useCallback(
    async (operation: (signal: AbortSignal) => Promise<unknown>): Promise<void> => {
      const previous = syncInFlightRef.current;
      const controller = syncLineageAbortRef.current ?? new AbortController();
      syncLineageAbortRef.current = controller;
      const promise = (async () => {
        if (previous) {
          await previous.catch(() => {});
        }
        // A submit drain aborted this lineage while this sync was waiting
        // behind an earlier PUT; never start the stale operation.
        if (controller.signal.aborted) return;
        await operation(controller.signal);
      })();
      syncInFlightRef.current = promise;
      void promise.finally(() => {
        if (syncInFlightRef.current === promise) {
          syncInFlightRef.current = null;
          syncLineageAbortRef.current = null;
        }
      });
      return promise;
    },
    [],
  );

  const reportError = useCallback((error: unknown) => {
    const message =
      error instanceof ApiClientError
        ? error.message
        : "Something went wrong. Your work is still in the editor — try again.";
    dispatch({ type: "guidance", message });
  }, []);

  // An aborted sync was cancelled because its lineage was superseded (e.g. a
  // submit drain gave up on it). The flow that cancelled it reports its own
  // outcome, so a stale PUT's abort must not overwrite that message with a
  // generic network error.
  const reportUnlessAborted = useCallback(
    (error: unknown) => {
      if (!isAbortError(error)) reportError(error);
    },
    [reportError],
  );

  /* ------------------------------------------------------------------ loading */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const workspace = await client.getWorkspace();
        if (cancelled) return;
        dispatch({ type: "loaded", workspace });
      } catch (error) {
        if (cancelled) return;
        dispatch({
          type: "error",
          message:
            error instanceof ApiClientError
              ? error.message
              : "Could not load your writing space.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client]);

  /* ------------------------------------------------------- background syncing */

  /** Pushes the draft snapshot to the service. Unguarded: the caller decides
   *  whether a submit is in flight. Fire-and-forget callers go through
   *  `syncDraft` (which bails while submitting); submit's own pre-sync uses
   *  this directly because it marks itself in-flight first. */
  const pushDraftSnapshot = useCallback(async () => {
    const current = stateRef.current;
    if (current.status !== "ready") return;

    await runSynced(async (signal) => {
      try {
        await client.syncDraft(
          {
            html: current.draft.html,
            text: current.draft.text,
            professorEmail: current.draft.professorEmail,
            studentEmail: current.draft.studentEmail,
            trackingActive: current.draft.trackingActive,
            metrics: current.draft.metrics,
            activity: current.draft.activity,
          },
          { signal },
        );
      } catch (error) {
        reportUnlessAborted(error);
      }
    });
  }, [client, reportUnlessAborted, runSynced]);

  const syncDraft = useCallback(async () => {
    // A debounced sync that fires while a submit is in flight would PUT the
    // pre-submit snapshot over the server's just-created successor draft: the
    // timers are only cancelled at submit start, so a keystroke during the
    // submitDraft POST re-arms them. The submit flow's own pre-sync runs the
    // raw `pushDraftSnapshot` path after marking itself in-flight, so this
    // guard only stops racing syncs.
    if (submittingRef.current) return;
    await pushDraftSnapshot();
  }, [pushDraftSnapshot]);

  const flushEvents = useCallback(async (): Promise<WritingEvent[]> => {
      // Only one events POST at a time: two overlapping flushes snapshot the
      // same buffer and would each dispatch it, duplicating the events on the
      // server. A caller that arrives mid-flight marks a re-run and AWAITS the
      // running flush's whole chain (including the deferred re-run that picks
      // up anything queued mid-flight), so it returns the TRUE remainder — what
      // is still undelivered. The snapshot reads stateRef, which is authoritative
      // at dispatch time, so events queued mid-flight (whose render has not yet
      // committed) are still picked up by the re-run. submit() consumes the
      // return value to re-queue the undelivered events into the successor
      // draft; re-queueing a delivered batch would POST it twice. pagehide/blur
      // ignore the return value, so the extra await is free for them.
      if (flushingRef.current) {
        flushAgainRef.current = true;
        return flushingRef.current;
      }

      // The chain promise: resolved when this flush and any deferred re-run
      // have settled, carrying the events that never reached the service.
      let resolveChain!: (remainder: WritingEvent[]) => void;
      const chain = new Promise<WritingEvent[]>((resolve) => {
        resolveChain = resolve;
      });
      flushingRef.current = chain;

      // Snapshot the buffer and POST it in sequential chunks, excluding the
      // previous flight's delivered events (lastSentRef). `eventsFlushed`
      // updates stateRef synchronously via the dispatch wrapper, so delivered
      // events are already gone from the authoritative buffer; the exclusion
      // stays as defense in depth.
      const run = async (): Promise<WritingEvent[]> => {
        const excludeSet = new Set(lastSentRef.current ?? []);
        const pending = stateRef.current.pendingEvents.filter((event) => !excludeSet.has(event));
        if (pending.length === 0) {
          return stateRef.current.pendingEvents.filter((event) => !excludeSet.has(event));
        }
        // The events endpoint caps each POST at MAX_EVENTS_PER_BATCH, so a
        // backlog that grew past the limit while the server was unreachable is
        // sent in sequential chunks. A failing chunk stops the loop and leaves
        // the rest buffered; only the chunks that actually reached the service
        // are counted as flushed, so the remainder is neither dropped nor
        // re-sent (which would duplicate it on the server) on the next flush.
        let sent = 0;
        for (let offset = 0; offset < pending.length; offset += MAX_EVENTS_PER_BATCH) {
          const batch = pending.slice(offset, offset + MAX_EVENTS_PER_BATCH);
          try {
            await client.appendEvents(batch);
            sent += batch.length;
          } catch {
            break;
          }
        }
        if (sent > 0) {
          // Identity-based removal, not a blind front slice: events recorded
          // while this flight was in flight (or the defensive cap trimming the
          // buffer's already-sent oldest events) mean the front of the live
          // buffer is no longer the batch that was actually delivered. The
          // reducer removes EXACTLY the sent identities from its authoritative
          // live buffer, so the newest events typed during the flight survive
          // for the next flush. Event objects are never re-created by the
          // reducer (the same references flow from logEvent into the buffer),
          // so Set identity works. The returned remainder is what this attempt
          // failed to deliver (or skipped as already delivered), for callers
          // that must not lose it.
          const sentEvents = pending.slice(0, sent);
          const sentSet = new Set(sentEvents);
          // Prune the exclusion set to identities still present in the live
          // buffer. stateRef is authoritative (dispatch-time), so the sent
          // events dropped out of it the moment eventsFlushed was dispatched;
          // whatever remains buffered stays excluded from the next snapshot.
          const stillBuffered = new Set(stateRef.current.pendingEvents);
          lastSentRef.current = [...(lastSentRef.current ?? []), ...sentEvents].filter((event) =>
            stillBuffered.has(event),
          );
          dispatch({ type: "eventsFlushed", sent: sentEvents });
          return stateRef.current.pendingEvents.filter(
            (event) => !sentSet.has(event) && !excludeSet.has(event),
          );
        }
        return stateRef.current.pendingEvents.filter((event) => !excludeSet.has(event));
      };

      try {
        let remainder = await run();
        // A flush arrived while this one was in flight. Defer its re-run until
        // after React has processed `eventsFlushed`, so it only sees events that
        // were genuinely queued during the flight rather than the batch this
        // POST just delivered. The re-run excludes this flight's delivered
        // events, so even if the deferred tick beats React's commit the batch is
        // not posted twice. If the coalescing caller was pagehide and the page
        // actually unloads, this re-run never executes and events queued
        // mid-flight are lost — the correct no-duplicate tradeoff, since a
        // second POST would re-deliver the batch that was already accepted.
        if (flushAgainRef.current) {
          flushAgainRef.current = false;
          await new Promise((resolve) => setTimeout(resolve, 0));
          remainder = await run();
        }
        resolveChain(remainder);
        return remainder;
      } catch {
        // Events stay buffered and go out with the next flush; settle the chain
        // so a caller that coalesced into this flush does not hang. Mirror the
        // sent===0 branch: exclude lastSentRef identities, so a throw after a
        // successful POST never hands already-delivered events back to a caller
        // (e.g. submit) that would re-queue them for another POST.
        const excludeSet = new Set(lastSentRef.current ?? []);
        const remainder = stateRef.current.pendingEvents.filter((event) => !excludeSet.has(event));
        resolveChain(remainder);
        return remainder;
      } finally {
        flushingRef.current = null;
        // A coalesced caller may have marked a re-run after this chain's
        // re-run check already passed (e.g. while it awaited the settled
        // chain); clear it so the next flush does not flush an empty buffer.
        flushAgainRef.current = false;
      }
    },
    [client],
  );

  const queueSync = useCallback(() => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      void syncDraft();
      void flushEvents();
    }, AUTOSAVE_DELAY_MS);
  }, [syncDraft, flushEvents]);

  /* ----------------------------------------------------------------- the clock */

  useEffect(() => {
    if (!autoStartClock) return;
    const interval = setInterval(() => {
      dispatch({ type: "tick", now: Date.now() });
    }, SESSION_TICK_MS);
    return () => clearInterval(interval);
  }, [autoStartClock]);

  // Closing (or backgrounding) the tab mid-typing must push what exists: the
  // debounced autosave may still be waiting on its timer when the page goes away.
  useEffect(() => {
    const flushNow = () => {
      if (stateRef.current.status !== "ready") return;
      // Cancel the armed debounces so they cannot fire after this explicit
      // flush and re-post the same buffered events (a second syncDraft is an
      // idempotent PUT, but a second flushEvents would duplicate the events).
      if (syncTimer.current) clearTimeout(syncTimer.current);
      if (promptTimer.current) clearTimeout(promptTimer.current);
      // A prompt edited inside the autosave window would otherwise be lost on
      // tab close: nothing else pushes it. Fire-and-forget like the draft
      // sync (best-effort; the browser stays authoritative for live values).
      // The draft sync must start FIRST: it chains immediately and its PUT is
      // initiated while the page's JS context is still alive, whereas Round 9
      // registered the prompt first, so the draft PUT only began once the
      // prompt PUT's response resolved — a chain continuation needs a live
      // context, which a teardown (or a stalled prompt PUT beyond its request
      // ceiling) can take away, losing the last ~1.5s of writing. The prompt
      // PUT chains behind it here. Ordering vs submit stays safe: syncDraft
      // bails while a submit is in flight, and flushNow fires only on
      // pagehide/visibility-hidden, which never starts a submit afterwards.
      void syncDraft();
      runSynced((signal) =>
        client
          .updateReferencePrompt(stateRef.current.referencePrompt, { signal })
          .catch(reportUnlessAborted),
      );
      void flushEvents();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushNow();
    };
    window.addEventListener("pagehide", flushNow);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushNow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [syncDraft, flushEvents, runSynced, reportUnlessAborted]);

  useEffect(
    () => () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      if (syncTimer.current) clearTimeout(syncTimer.current);
      if (promptTimer.current) clearTimeout(promptTimer.current);
    },
    [],
  );

  /* ---------------------------------------------------------------- the actions */

  const saveDraft = useCallback<StudentActions["saveDraft"]>(
    async () => {
      // A manual save racing an in-flight submit must not land its snapshot
      // after submit's clearSessions: that would resurrect a stale pre-submit
      // session in the dropdown and localStorage after the wipe.
      if (submittingRef.current) {
        if (confirmTimer.current) clearTimeout(confirmTimer.current);
        return;
      }
      // Capture the submit generation at entry. Unlike `submittingRef` (which
      // a COMPLETED submit clears in its finally), the epoch stays bumped, so
      // a save whose sync PUT resolves after the whole submit flow finished is
      // still detected and never re-populates the wiped sessions.
      const saveEpoch = submitEpochRef.current;
      // The current content goes to the service first (server state), then a
      // snapshot is kept in this browser for the Saved sessions dropdown.
      await syncDraft();
      // The sync above yields; a submit may have started while it was in
      // flight (epoch bumped at submit entry, and any in-flight sync is
      // awaited there too). Re-check before persisting so a manual save can
      // never re-populate localStorage after submit's wipe — whether the
      // submit is still running or has fully completed.
      if (submitEpochRef.current !== saveEpoch) return;
      // Drain any sync that started while this save's PUT was on the wire so
      // the persisted snapshot reflects a settled server write order.
      if (syncInFlightRef.current) {
        await syncInFlightRef.current;
        // The drain yields too: a submit that started while this save was
        // parked on it (epoch bumped) may have completed and wiped the
        // sessions. Re-check so a save resuming after the wipe never
        // re-populates the dropdown or localStorage.
        if (submitEpochRef.current !== saveEpoch) return;
      }
      const current = stateRef.current;
      const entry: DraftSession = {
        id: createSessionId(),
        savedAt: new Date().toISOString(),
        html: current.draft.html,
        text: current.draft.text,
        wordCount: current.draft.metrics.wordCount,
        // A deep copy, so later typing never mutates the saved snapshot.
        metrics: structuredClone(current.draft.metrics),
        // The assignment notes ride along so a restore brings the prompt back.
        referencePrompt: current.referencePrompt,
      };
      const sessions = persistDraftSession(entry, undefined, (warning) => {
        // Storage refused the snapshot; say so without blocking the save flow.
        dispatch({ type: "guidance", message: warning });
      });
      dispatch({ type: "sessionsUpdated", sessions });
      // A fresh save becomes the selected session: the dropdown names the
      // snapshot that was just taken, and the next save creates a new entry.
      dispatch({ type: "sessionSelected", id: entry.id });
      dispatch({ type: "saveStatus", value: "Draft saved" });

      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => {
        // Autosave keeps content synced on its own debounce; this timer only
        // settles the manual-save confirmation back to "Saved".
        dispatch({ type: "saveStatus", value: "Saved" });
      }, SAVE_CONFIRM_MS);
    },
    [syncDraft],
  );

  /** Puts a saved session's words into the editor and pushes them to the service. */
  const restoreSession = useCallback(
    async (session: DraftSession) => {
      // A restore racing a submit would land its `sessionLoaded` and syncDraft
      // PUT after the submit reset, resurrecting content the submission just
      // cleared and wiping the user's restore via `submitted`. The epoch is
      // captured up front: unlike `submittingRef` (cleared when a submit
      // finishes), it stays bumped, so a restore that resumes after the whole
      // submit flow completed is still detected.
      const restoreEpoch = submitEpochRef.current;
      if (submittingRef.current) return;

      // Restoring resets the prompt source of truth, so cancel the armed
      // autosave timers: a still-armed promptTimer would push the pre-restore
      // typed value and win over the restored snapshot on the next reload.
      if (syncTimer.current) clearTimeout(syncTimer.current);
      if (promptTimer.current) clearTimeout(promptTimer.current);
      // Flush first: `sessionLoaded` resets the event buffer, so anything still
      // queued would otherwise be dropped with the restored draft.
      const leftover = await flushEvents();
      // A submit may have started while this restore was held on its flush.
      // Applying the restored session now would resurrect the pre-submit draft
      // and selected session over the fresh post-submit one, and drop
      // currentSubmissionId — even if the submit has since fully completed.
      if (submitEpochRef.current !== restoreEpoch) return;
      dispatch({ type: "sessionLoaded", session });
      // A flush that failed leaves its events buffered, but `sessionLoaded`
      // wiped the buffer above; re-queue whatever could not be delivered into
      // the restored draft's buffer, exactly like submit's post-submit re-queue
      // (the restored draft keeps tracking, so the next flush delivers them).
      if (leftover.length > 0) {
        dispatch({ type: "eventsQueued", events: leftover });
      }
      // A submit that started during the flush (epoch bumped) must not let this
      // PUT land after the submit's own pre-sync: that would push the restored
      // content over the server's successor draft.
      if (submitEpochRef.current !== restoreEpoch) return;
      // Drain any sync already on the wire before the restore's own sync, so
      // the restored snapshot is written after every earlier PUT and cannot be
      // overwritten by one that was already in flight.
      if (syncInFlightRef.current) {
        await syncInFlightRef.current;
        // The drain yields: a submit that started while this restore was
        // parked on it (epoch bumped at submit entry) may have completed and
        // reset the workspace. Stop before the restored snapshot is pushed or
        // announced over the fresh post-submit state.
        if (submitEpochRef.current !== restoreEpoch) return;
      }
      await syncDraft();
      // This sync yields too; a submit that started while the restored words
      // were on the wire must not get a late "Restored saved session…" note in
      // the reset workspace (or a restored PUT landing after the successor).
      if (submitEpochRef.current !== restoreEpoch) return;
      dispatch({
        type: "guidance",
        message: `Restored saved session from ${formatSavedAt(session.savedAt)}.`,
      });
    },
    [flushEvents, syncDraft],
  );

  /**
   * Fetches the stored PDF report for a submission. Best-effort: a failure is
   * logged but never surfaced as a guidance error, so it can never overwrite
   * the submission confirmation.
   */
  const downloadPdf = useCallback<StudentActions["downloadPdf"]>(async (submissionId) => {
    try {
      return await client.downloadSubmissionPdf(submissionId);
    } catch (error) {
      console.error("Could not fetch the submission PDF:", error);
      return null;
    }
  }, [client]);

  const actions = useMemo<StudentActions>(
    () => ({
      setProfessorEmail(value) {
        dispatch({ type: "setProfessorEmail", value });
        queueSync();
      },

      setStudentEmail(value) {
        dispatch({ type: "setStudentEmail", value });
        queueSync();
      },

      setReferencePrompt(value) {
        dispatch({ type: "setReferencePrompt", value });
        // A prompt edit rides on its own timer so it never clears a queued
        // draft/email sync (which also flushes buffered events).
        if (promptTimer.current) clearTimeout(promptTimer.current);
        promptTimer.current = setTimeout(() => {
          // Guarded like syncDraft: a prompt edited during a submit must not
          // PUT the pre-submit prompt over the workspace after submitDraft
          // resolved. submit() pushes the prompt itself before the POST.
          if (submittingRef.current) return;
          runSynced((signal) =>
            client.updateReferencePrompt(value, { signal }).catch(reportUnlessAborted),
          );
        }, AUTOSAVE_DELAY_MS);
      },

      async startAssignment() {
        // The professor's email must reach the service before it can open a session.
        // Note: this sync is silently suppressed while a submit is in flight
        // (syncDraft bails on submittingRef). Reachable after `submitted` resets
        // trackingActive while the PDF still downloads; impact is nil — the
        // server's successor draft is already authoritative.
        await syncDraft();
        try {
          const draft = await client.startSession();
          dispatch({ type: "sessionStarted", draft });
        } catch (error) {
          reportError(error);
        }
      },

      saveDraft,

      async loadSession(id) {
        const session = stateRef.current.sessions.find((entry) => entry.id === id);
        if (!session) {
          dispatch({ type: "guidance", message: "That saved session is no longer available." });
          return;
        }
        await restoreSession(session);
      },

      deleteSession(id) {
        const current = stateRef.current;
        const sessions = removeStoredSession(id);
        dispatch({ type: "sessionsUpdated", sessions });

        // Deleting a session that is not the one in the editor only changes the
        // dropdown list.
        if (id !== current.selectedSessionId) return;

        if (sessions.length === 0) {
          // Nothing left to load: the editor returns to a brand-new draft.
          dispatch({ type: "draftCleared" });
          return;
        }

        // Auto-load the newest remaining session so the editor and the select
        // stay consistent with the dropdown.
        void restoreSession(sessions[0]!);
      },

      async submit() {
        const current = stateRef.current;
        if (!current.draft.trackingActive) {
          dispatch({
            type: "guidance",
            message:
              "Start the assignment before submitting so the writing session can be recorded.",
          });
          return;
        }
        if (!isSinclairEmail(current.draft.professorEmail)) {
          dispatch({
            type: "guidance",
            message: "Enter your professor's Sinclair email before submitting your work.",
          });
          return;
        }
        if (!isSinclairEmail(current.draft.studentEmail)) {
          dispatch({
            type: "guidance",
            message: "Enter your Sinclair email before submitting your work.",
          });
          return;
        }

        // A double-click must not run the whole flow twice: the server coalesces
        // the POSTs, but each client invocation would dispatch the confirmation
        // and download the PDF again. Guard the flow and clear it in a finally.
        if (submittingRef.current) return;
        // Mark the flow in-flight BEFORE the pre-submit sync: a debounced sync
        // that fires while that sync is in flight (submittingRef still false,
        // timers not yet cancelled) would otherwise start a second PUT that
        // could land after the submit POST. With the flag set here, debounced
        // syncs skip, and the pre-sync runs the raw `pushDraftSnapshot` path
        // instead of the guarded `syncDraft`. A double click still collapses at
        // the guard above — the second invocation sees submittingRef already set.
        submittingRef.current = true;
        // Bump the submit epoch at entry: anything that captured the old epoch
        // before this submit (e.g. a restore parked on a flush) can detect that
        // a submit happened, whether it is still running or fully completed.
        submitEpochRef.current += 1;

        // Cancel any pending debounced sync so the pre-submit draft (tracking
        // still on) is pushed in a known order. The prompt has its own timer,
        // so it is cancelled here too rather than pushing a stale prompt after
        // the workspace is reset.
        if (syncTimer.current) clearTimeout(syncTimer.current);
        if (promptTimer.current) clearTimeout(promptTimer.current);

        try {
          // Drain any draft/prompt PUT that was already on the wire when this
          // submit began, so it cannot resolve after the successor is saved
          // (the server has no generation check; last writer wins). The chain
          // includes any sync started before submit's entry — no NEW sync can
          // start while submittingRef is set.
          if (syncInFlightRef.current) {
            // Bound the drain: without a ceiling an in-flight PUT on a
            // blackholed connection would otherwise park the submit forever
            // with an unresponsive button. Race the drain against a short
            // window; if the window wins, CANCEL the stuck chain's request so
            // it can never land after the successor is saved, drop the chain
            // (keeping it would hang every later runSynced behind it too) and
            // continue with a stall note. Trade-off: the stale-PUT ordering
            // guarantee only reopens in the already-pathological blackhole
            // case, and last writer wins means completing the submit beats a
            // silent hang.
            const drain = syncInFlightRef.current;
            const drainTimeout = new Promise<"timeout">((resolve) => {
              setTimeout(() => resolve("timeout"), SYNC_DRAIN_TIMEOUT_MS);
            });
            const outcome = await Promise.race([
              drain.then(() => "drain" as const, () => "drain" as const),
              drainTimeout,
            ]);
            if (outcome === "timeout") {
              // Abort the whole abandoned lineage: the request actually on the
              // wire AND any sync still waiting behind it must never run now.
              syncLineageAbortRef.current?.abort();
              syncLineageAbortRef.current = null;
              syncInFlightRef.current = null;
              dispatch({ type: "guidance", message: SYNC_STALL_GUIDANCE });
            }
          }
          // The pre-submit snapshot is pushed here, on the unguarded path: the
          // flow is already marked in-flight, so syncDraft would no-op.
          await pushDraftSnapshot();
          // A prompt edited within the autosave window must be part of the
          // submission's snapshot: the service copies the current
          // referencePrompt into the submission, so push the latest value
          // before the submit POST. Best-effort — a failure must not abort
          // the submission, and the server falls back to its stored prompt.
          await runSynced((signal) =>
            client
              .updateReferencePrompt(stateRef.current.referencePrompt, { signal })
              .catch(reportUnlessAborted),
          );
          await flushEvents();

          const result = await client.submitDraft();
          const submissionId = result.submission.submissionId;
          // Keystrokes that landed while the request was in flight were buffered;
          // push them before `submitted` wipes the buffer on the fresh draft.
          let leftover = await flushEvents();
          // This flush is the only delivery chance for those mid-POST events: if
          // it failed (a rejected chunk), give delivery one more retry before the
          // buffer wipe destroys the evidence.
          if (leftover.length > 0) {
            leftover = await flushEvents();
          }
          // The flush snapshots stateRef (authoritative at dispatch time), so an
          // event dispatched mid-POST is in the snapshot even before its render
          // commits — it is delivered here or surfaces in `leftover`, never
          // silently dropped by the wipe below.
          dispatch({ type: "submitted", submission: result.submission, draft: result.draft });

          // A post-submit flush that kept failing must not silently drop the
          // mid-POST keystrokes: re-queue whatever is still undelivered into the
          // successor draft so the next flush delivers them.
          if (leftover.length > 0) {
            dispatch({ type: "eventsQueued", events: leftover });
          }

          // Requirement: submitting wipes every saved draft session from this
          // browser so the next draft starts clean. The status-bar submission ID
          // and the PDF copy survive because they are part of the submission.
          clearSessions();
          dispatch({ type: "sessionsUpdated", sessions: [] });

          // Deliver the student's PDF copy in the browser. Best-effort: if the
          // fetch fails the submission is already confirmed, so we log and move
          // on rather than turning a successful submit into an error state.
          const blob = await downloadPdf(submissionId);
          if (blob) triggerBlobDownload(blob, submissionPdfFileName(submissionId));
        } catch (error) {
          reportError(error);
        } finally {
          submittingRef.current = false;
        }
      },

      recordMutation(input) {
        dispatch({
          type: "mutation",
          html: input.html,
          text: input.text,
          source: input.source,
          now: new Date().toISOString(),
          paragraphCount: input.paragraphCount,
        });
        // Continuous typing must reach the server on a debounce, not only on
        // blur/manual save: this pushes the newest draft content (and flushes
        // buffered events) once typing pauses.
        queueSync();
      },

      recordKeydown(key) {
        dispatch({ type: "keydown", key, now: new Date().toISOString() });
      },

      recordPaste(characters, words) {
        dispatch({ type: "paste", characters, words, now: new Date().toISOString() });
      },

      recordFocus() {
        dispatch({ type: "editorFocus", now: new Date().toISOString() });
      },

      recordBlur() {
        dispatch({ type: "editorBlur", now: new Date().toISOString() });
        void syncDraft();
        void flushEvents();
      },

      setGuidance(message) {
        dispatch({ type: "guidance", message });
      },

      downloadPdf,
    }),
    [client, downloadPdf, flushEvents, pushDraftSnapshot, queueSync, reportError, reportUnlessAborted, restoreSession, runSynced, saveDraft, syncDraft],
  );

  const review = useMemo(() => evaluateMetrics(state.draft.metrics), [state.draft.metrics]);

  const value = useMemo<StudentContextValue>(
    () => ({ state, review, actions, client }),
    [state, review, actions, client],
  );

  return <StudentContext.Provider value={value}>{children}</StudentContext.Provider>;
}

function formatSavedAt(value: string): string {
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
  });
}

export function useStudent(): StudentContextValue {
  const context = useContext(StudentContext);
  if (!context) {
    throw new Error("useStudent must be used inside a StudentProvider.");
  }
  return context;
}
